import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar as CalendarIcon, Video, Building2, ExternalLink, MapPin, Users, Sparkles, RefreshCw, Link2Off, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatDateTimeBR } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

function MeetingsList({ meetings, contracts, basePath = "/secretaria" }) {
  if (meetings.length === 0) {
    return <div className="text-sm text-slate-500 py-6 text-center">Nenhuma reunião.</div>;
  }
  return (
    <div className="space-y-3">
      {meetings.map((m) => {
        const contract = contracts.find((c) => c.id === m.contract_id);
        const consultantName = m.consultant_name || contract?.consultant_name;
        return (
          <Link
            key={m.id}
            to={`${basePath}/reunioes/${m.id}`}
            className="block p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
            data-testid={`meeting-row-${m.id}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Video className="w-4 h-4 text-slate-500" />
                  <span className="font-medium text-slate-900">{m.title}</span>
                  <StatusBadge status={m.status} />
                  {m.google_calendar_event_id && (
                    <Badge variant="outline" className="border-emerald-300 text-emerald-700 text-[10px] py-0">
                      <CalendarIcon className="w-3 h-3 mr-1" /> sincronizada
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {formatDateTimeBR(m.scheduled_at)} · {m.duration_minutes} min
                </div>
                {contract && (
                  <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    {contract.client_name} · {contract.product_name}
                  </div>
                )}
                {consultantName && (
                  <div className="text-xs mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-midnight/5 text-midnight" data-testid={`meeting-consultant-${m.id}`}>
                    <User className="w-3 h-3" /> Consultor responsável: <strong className="font-semibold">{consultantName}</strong>
                  </div>
                )}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function groupByDate(events) {
  const map = new Map();
  for (const ev of events) {
    if (!ev.start) continue;
    const d = new Date(ev.start);
    const key = d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(ev);
  }
  return Array.from(map.entries());
}

function GoogleEventRow({ ev }) {
  const start = ev.start ? new Date(ev.start) : null;
  const end = ev.end ? new Date(ev.end) : null;
  const timeLabel = ev.all_day
    ? "Dia inteiro"
    : start
      ? `${start.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}${end ? ` – ${end.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : ""}`
      : "—";

  return (
    <div
      className="flex items-start gap-4 p-4 border border-slate-200 rounded-lg hover:border-midnight/40 hover:bg-slate-50 transition-colors"
      data-testid={`google-event-${ev.id}`}
    >
      <div className="flex-shrink-0 w-20 text-right">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">
          {ev.all_day ? "Dia inteiro" : timeLabel.split("–")[0]}
        </div>
        {!ev.all_day && end && (
          <div className="text-[11px] text-slate-400">até {end.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
        )}
      </div>
      <div className="w-px self-stretch bg-slate-200" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-medium text-slate-900 truncate">{ev.title}</h4>
          {ev.is_ceap_meeting && (
            <Badge className="bg-midnight text-white hover:bg-midnight text-[10px]">CEAP</Badge>
          )}
          {ev.status === "tentative" && (
            <Badge variant="outline" className="border-amber-300 text-amber-700 text-[10px]">Provisório</Badge>
          )}
          {ev.status === "cancelled" && (
            <Badge variant="outline" className="border-red-300 text-red-700 text-[10px]">Cancelado</Badge>
          )}
        </div>
        {ev.description && (
          <p className="text-xs text-slate-600 mt-1 line-clamp-2 whitespace-pre-wrap">{ev.description}</p>
        )}
        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
          {ev.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {ev.location.length > 60 ? ev.location.slice(0, 60) + "…" : ev.location}
            </span>
          )}
          {ev.attendees?.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <Users className="w-3 h-3" /> {ev.attendees.length} participante{ev.attendees.length === 1 ? "" : "s"}
            </span>
          )}
          {ev.hangout_link && (
            <a
              href={ev.hangout_link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
            >
              <Video className="w-3 h-3" /> Meet
            </a>
          )}
          {ev.html_link && (
            <a
              href={ev.html_link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-midnight hover:underline"
            >
              Abrir no Google <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function GoogleAgenda({ basePath }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/google-calendar/events?days=${days}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const grouped = useMemo(() => (data?.events ? groupByDate(data.events) : []), [data]);

  if (data === null) return <Skeleton className="h-72" />;

  // Not connected → CTA
  if (!data.connected) {
    return (
      <div className="text-center py-12 px-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-midnight/5 mb-4">
          <CalendarIcon className="w-7 h-7 text-midnight" />
        </div>
        <h3 className="text-base font-semibold text-slate-900">Agenda Google ainda não conectada</h3>
        <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
          {data.configured
            ? "As credenciais já estão salvas. Conecte a conta Google em Configurações para visualizar a agenda institucional aqui."
            : "Configure o Google Calendar nas Configurações da Diretora para sincronizar e visualizar a agenda."}
        </p>
        {basePath === "/diretora" ? (
          <Link to="/diretora/configuracoes">
            <Button className="mt-4 bg-midnight hover:bg-ink text-white" data-testid="connect-from-agenda">
              <Link2Off className="w-4 h-4 mr-2 rotate-180" /> Ir para Configurações
            </Button>
          </Link>
        ) : (
          <p className="text-xs text-slate-500 mt-3">
            Solicite à Diretora para concluir a conexão em <strong>Configurações &rarr; Conectar Google Calendar</strong>.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Sparkles className="w-4 h-4 text-emerald-600" />
          <span>Conectado como <strong>{data.email}</strong></span>
          <Badge variant="outline" className="border-emerald-300 text-emerald-700 ml-2">{data.events.length} eventos</Badge>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-xs border border-slate-200 rounded px-2 py-1"
            data-testid="google-agenda-days"
          >
            <option value={7}>Próximos 7 dias</option>
            <option value={14}>Próximos 14 dias</option>
            <option value={30}>Próximos 30 dias</option>
            <option value={60}>Próximos 60 dias</option>
            <option value={90}>Próximos 90 dias</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="google-agenda-refresh"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
        </div>
      </div>

      {data.events.length === 0 ? (
        <div className="text-sm text-slate-500 py-12 text-center border border-dashed border-slate-200 rounded-lg">
          Nenhum evento nos próximos {days} dias.
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([dateLabel, evs]) => (
            <div key={dateLabel}>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 first-letter:uppercase">
                {dateLabel}
              </h4>
              <div className="space-y-2">
                {evs.map((ev) => (
                  <GoogleEventRow key={ev.id} ev={ev} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Agenda({ basePath = "/secretaria" }) {
  const [upcoming, setUpcoming] = useState(null);
  const [past, setPast] = useState(null);
  const [contracts, setContracts] = useState([]);

  useEffect(() => {
    api.get("/meetings", { params: { upcoming: true } }).then(({ data }) => setUpcoming(data));
    api.get("/meetings", { params: { past: true } }).then(({ data }) => setPast(data));
    api.get("/contracts").then(({ data }) => setContracts(data));
  }, []);

  if (!upcoming || !past) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Agenda &amp; Reuniões</h1>
        <p className="text-sm text-slate-600 mt-1">
          Reuniões agendadas pelo CEAP e eventos sincronizados com a agenda Google institucional.
        </p>
      </div>

      <Tabs defaultValue="upcoming">
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">
            <CalendarIcon className="w-4 h-4 mr-2" /> Próximas ({upcoming.length})
          </TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past">
            Passadas ({past.length})
          </TabsTrigger>
          <TabsTrigger value="google" data-testid="tab-google-agenda">
            <Sparkles className="w-4 h-4 mr-2" /> Agenda Google
          </TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming">
          <Card className="card-soft">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">Próximas reuniões do CEAP</CardTitle>
            </CardHeader>
            <CardContent>
              <MeetingsList meetings={upcoming} contracts={contracts} basePath={basePath} />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="past">
          <Card className="card-soft">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">Reuniões realizadas</CardTitle>
            </CardHeader>
            <CardContent>
              <MeetingsList meetings={past} contracts={contracts} basePath={basePath} />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="google">
          <Card className="card-soft">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-midnight" /> Agenda do Google Calendar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <GoogleAgenda basePath={basePath} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
